import { type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export type InputSize = 'sm' | 'md' | 'lg';

export interface BaseInputProps {
  /** Label for the input */
  label?: string;
  /** Error message or boolean to indicate error state for styling */
  error?: string | boolean;
  /** Helper text shown below input */
  helperText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Icon to show on the left side */
  leftIcon?: ReactNode;
  /** Icon to show on the left side (alias for leftIcon) */
  icon?: ReactNode;
  /** Icon to show on the right side */
  rightIcon?: ReactNode;
  /** Input size */
  inputSize?: InputSize;
  /** Make input full width */
  fullWidth?: boolean;
  /** Wrapper class name */
  wrapperClassName?: string;
  /** Use gray background style (no border) */
  grayBackground?: boolean;
}

// ============================================================================
// Styles - Using design tokens from index.css
// ============================================================================

export const baseInputStyles = `
  block w-full rounded-[var(--radius-md)] border
  bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]
  placeholder:text-[var(--color-text-disabled)]
  focus:outline-none focus:ring-2 focus:ring-offset-0
  disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed
  transition-colors duration-[var(--transition-fast)]
  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
`;

/**
 * Размеры полей. Кегль — 16 px на телефоне и 14 px от планшета (решение владельца 26.08.2026).
 *
 * **До этого кегля у полей не было ВООБЩЕ.** Здесь стояло `text-[var(--text-sm)]`, а такая запись
 * двусмысленна: `text-[…]` — это и цвет, и размер, и Tailwind разбирает переменную как ЦВЕТ. В
 * собранный CSS уходило `color: var(--text-sm)` — значение невалидное, браузер его выбрасывал, —
 * а `font-size` не появлялся ни разу. Все поля обоих продуктов наследовали корневые 17.6 px.
 * Наступали дважды: 25.08 на форме создания заявки (обошли локальным `text-md`) и 26.08 в
 * панели фильтров кабинета. Арифметика в квадратных скобках (`text-touch`) однозначна — это
 * длина, и она разбирается как размер.
 *
 * **16 на телефоне — не вкусовщина.** iOS Safari масштабирует страницу при фокусе в поле с кеглем
 * меньше 16 px. Прежние случайные 17.6 px от этого защищали; любой «правильный» размер ниже 16
 * вернул бы зум на каждом поле, а кабинет ставится на домашний экран.
 */
export const inputSizeStyles: Record<InputSize, string> = {
  sm: 'px-[var(--spacing-3)] py-[var(--spacing-2)] text-touch md:text-md',
  md: 'px-[var(--spacing-3)] py-[10px] text-touch md:text-md',
  lg: 'px-[var(--spacing-4)] py-[var(--spacing-3)] text-touch md:text-md',
};

/**
 * Высота ОДНОСТРОЧНОГО поля — та же переменная, из которой её берёт кнопка (27.08.2026): 44 px на
 * телефоне, 40 на компьютере. Разбор — в комментарии к `--h-control` в `index.css`.
 *
 * Отдельной картой, а не строкой внутри {@link inputSizeStyles}, ровно по одной причине:
 * `Textarea` берёт те же размеры, и фиксированная высота схлопнула бы её в одну строку. Поэтому
 * высоту накладывают только `TextInput` и `Select`, а многострочное поле остаётся на своих
 * отступах и `min-h-[80px]`.
 *
 * `h-`, а не `min-h-`: у `md` вертикальные отступы дают 42.3 px сами по себе, и минимум в 40 их
 * не опустил бы — поле осталось бы выше кнопки, то есть ровно тем, что чинится.
 */
export const fieldHeightStyles: Record<InputSize, string> = {
  sm: 'h-[var(--h-control)]',
  md: 'h-[var(--h-control)]',
  lg: 'h-[var(--h-control-lg)]',
};

export const inputStateStyles = {
  default: 'border-[var(--color-border)] focus:border-accent-500 focus:ring-accent-500/20',
  error: 'border-[var(--color-error)] focus:border-[var(--color-error)] focus:ring-[var(--color-error)]/20',
};
